<script setup lang="ts">
import {computed} from "vue";
import {Textarea} from "@/core/components/ui/textarea";
import {Label} from "@/core/components/ui/label";
import MedicationCheckupYesNo from "@/medication-checkup/components/MedicationCheckupYesNo.vue";
import {KnowledgeAppraisalAnswers, KnowledgeAppraisalView} from "@/medication-checkup/interfaces/knowledge-appraisal";

/**
 * The appraisal of what the patient knows of their treatments, asked at the
 * closing of each interview the patient attends: a yes/no, and the reason
 * once the level is judged unsatisfactory. It lands on the conclusions form
 * of the year.
 *
 * The answer is the card's above: live, the field shows what it is handed
 * and tells each change; frozen, it reads as one line from what the server
 * recorded, and nothing for a closing validated before it was asked — or
 * served by a server that does not record the appraisal yet.
 */

const props = defineProps<{
    /**
     * The appraisal the server froze, once the block is completed.
     */
    recorded: KnowledgeAppraisalView;
    frozen?: boolean;
    /**
     * The appraisal as it stands, while the closing is live.
     */
    appraisal?: KnowledgeAppraisalAnswers;
}>();

const emit = defineEmits<{
    appraised: [appraisal: KnowledgeAppraisalAnswers];
}>();

const LABEL = 'Niveau de connaissance du patient satisfaisant ?';

const REASON = 'Pourquoi ?';

/**
 * The recorded appraisal in words, the reason following a level judged
 * unsatisfactory.
 */
const recordedLine = computed<string>(() => {
    if (props.recorded.knowledgeable) {
        return 'Niveau de connaissance satisfaisant';
    }

    return props.recorded.knowledge_gap
        ? `Niveau de connaissance insuffisant : ${props.recorded.knowledge_gap}`
        : 'Niveau de connaissance insuffisant';
});

const appraise = (change: Partial<KnowledgeAppraisalAnswers>) => {
    emit('appraised', {knowledgeable: true, knowledge_gap: '', ...props.appraisal, ...change});
};
</script>

<template>
  <div
    v-if="frozen && typeof recorded.knowledgeable === 'boolean'"
    class="flex flex-col gap-1.5"
  >
    <Label class="text-sm font-semibold">Appréciation</Label>
    <span class="text-sm text-gray-dark whitespace-pre-line">{{ recordedLine }}</span>
  </div>

  <div
    v-else-if="!frozen && appraisal"
    class="flex flex-col gap-1.5"
  >
    <div class="flex flex-wrap items-center justify-between gap-2">
      <Label class="text-sm font-semibold">{{ LABEL }}</Label>
      <MedicationCheckupYesNo
        :answer="appraisal.knowledgeable"
        @answer="appraise({knowledgeable: $event})"
      />
    </div>

    <Textarea
      v-if="!appraisal.knowledgeable"
      :aria-label="REASON"
      :model-value="appraisal.knowledge_gap"
      :placeholder="REASON"
      :rows="2"
      class="min-h-0 rounded-lg bg-white shadow-none resize-none focus-visible:ring-0 focus-visible:border-primary"
      @update:model-value="appraise({knowledge_gap: String($event)})"
    />
  </div>
</template>
